import React, { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  showDoneButton?: boolean; // Optional prop to show Done button
  onBackClick?: () => void; // Optional prop for back button
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl'; // Optional size prop
  /** When true, clicking the backdrop (outside the content) calls onClose. Default false for edit flows. */
  closeOnBackdropClick?: boolean;
  /** When this value changes while open, reset the scrollable body to the top. */
  bodyScrollResetKey?: string | number;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  showDoneButton = false, // Default to false
  onBackClick, // Receive back handler
  size = 'xl', // Default size
  closeOnBackdropClick = false,
  bodyScrollResetKey,
}) => {
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    bodyRef.current?.scrollTo({ top: 0 });
  }, [bodyScrollResetKey, isOpen]);

  if (!isOpen) return null;

  // Determine width class based on size prop
  const getSizeClass = () => {
    switch (size) {
      case 'sm': return 'max-w-sm';
      case 'md': return 'max-w-md';
      case 'lg': return 'max-w-lg';
      case 'xl': return 'max-w-xl';
      case '2xl': return 'max-w-2xl';
      case '3xl': return 'max-w-3xl';
      case '4xl': return 'max-w-4xl';
      case '5xl': return 'max-w-5xl';
      default: return 'max-w-xl'; // Default fallback
    }
  };

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={closeOnBackdropClick ? onClose : undefined}
      role={closeOnBackdropClick ? 'button' : undefined}
      aria-label={closeOnBackdropClick ? 'Close modal' : undefined}
    >
      {/* Apply size class here */}
      <div 
        className={`bg-white rounded-lg shadow-xl w-full ${getSizeClass()} flex flex-col max-h-[90vh]`}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center">
            {onBackClick && (
              <button 
                onClick={onBackClick} 
                className="p-1 mr-3 rounded-full hover:bg-gray-200 text-gray-600"
                title="Back"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200 text-gray-600" title="Close">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        {/* Modal Body */}
        <div ref={bodyRef} className="p-6 overflow-y-auto flex-grow">
          {children}
        </div>
        {/* Modal Footer - Only shown if Done button is needed */}
        {showDoneButton && (
           <div className="flex justify-end p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                 onClick={onClose}
                 className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                 Done
              </button>
           </div>
        )}
      </div>
    </div>
  );

  // Render modal directly to document.body using React Portal
  return createPortal(modalContent, document.body);
};

export default Modal; 
